import Cookies from 'universal-cookie';

import { useProfile } from '../../hooks';
import { isB2b } from '../../utils';

const getBasketIdFromCookie = () => {
  const cookies = new Cookies();
  const basketId = cookies.get('basketId');

  return basketId ? String(basketId) : undefined;
};

const setBasketIdToCookie = (basketId: string) => {
  const cookies = new Cookies();

  cookies.set('basketId', basketId, { path: '/' });
};

const deleteBasketIdFromCookie = () => {
  const cookies = new Cookies();

  cookies.remove('basketId', { path: '/' });
};

/**
 * Hook to manage the anonymous basket id in a cookie
 * IMPORTANT: This hook is designed for GUEST USERS ONLY (not logged-in users)
 * - Logged-in users should rely on their accountId to fetch baskets
 * - Anonymous basket cookies are only for guest users without accounts
 *
 * @returns {Object} anonymousBasketId, setAnonymousBasketId, deleteAnonymousBasketId, getAnonymousBasketId
 * @returns {string|null} anonymousBasketId - The anonymous basket id (null for logged-in users)
 * @returns {function} setAnonymousBasketId - Function to set the anonymous basket id (no-op for logged-in users)
 * @returns {function} deleteAnonymousBasketId - Function to delete the anonymous basket id
 * @returns {function} getAnonymousBasketId - Function to get the anonymous basket id from cookie
 **/

export const useAnonymousBasket = () => {
  const { isLoggedIn } = useProfile();

  const anonymousBasketAllowed = !isB2b && !isLoggedIn;
  const anonymousBasketId = anonymousBasketAllowed ? getBasketIdFromCookie() : null;

  /**
   * Sets the anonymous basket ID in a cookie
   */
  const setAnonymousBasketId = (basketId: string) => {
    if (!anonymousBasketAllowed) {
      return;
    }
    setBasketIdToCookie(basketId);
  };

  /**
   * Deletes the anonymous basket ID cookie
   */
  const deleteAnonymousBasketId = () => {
    deleteBasketIdFromCookie();
  };

  return {
    anonymousBasketId,
    setAnonymousBasketId,
    deleteAnonymousBasketId,
    getAnonymousBasketId: getBasketIdFromCookie,
  };
};
